import { Modal, Box, Grid, Typography, Button, TextField, CircularProgress, Checkbox } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Story } from "@theirstory/common/api_types/backend";

import TSSecondaryButton from "../../../components/common/buttons/TSSecondaryButton";
import useMixPanel from "../../../lib/mixpanel";
import { notifyError, notifySuccess } from "../../../store/actions/notify";
import type { AppDispatch } from "../../../store/state/rootStore";
import type { RootState } from "../../../store/state/rootReducer";
import { makeStyles } from "../../../theme/muiThemeInterview";
import { getStoryDisplayTitle } from "../../../utils/storyDisplayHelpers";

import {
  DEFAULT_SUMMARY_SYSTEM_PROMPT,
  DEFAULT_SUMMARY_USER_PROMPT,
  getEligibleAIStoryIds,
  isStoryEligibleForAIGeneration,
  requestSummaryGeneration,
} from "./aiGenerationUtils";

interface GPTSummaryModalProps {
  showModal: boolean;
  onCancel: () => void;
  storyIds: string[];
  stories?: Story[];
  onComplete?: () => void;
}

const useClasses = makeStyles()(() => ({
  textArea: {
    "& .MuiInputBase-input": {
      "&::-webkit-scrollbar": {
        width: "12px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#606971",
        borderRadius: "10px",
        outline: "transparent",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
    },
  },
}));

export const GPTSummaryModal = ({ showModal, onCancel, storyIds, stories, onComplete }: GPTSummaryModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SUMMARY_SYSTEM_PROMPT);
  const [userPrompt, setUserPrompt] = useState(DEFAULT_SUMMARY_USER_PROMPT);
  const [isLoading, setIsLoading] = useState(false);
  const userFlags = useSelector((state: RootState) => state.featureFlags?.flags);
  const { classes } = useClasses();
  const targetStoryIds = storyIds;
  const isBulk = targetStoryIds.length > 1;
  const eligibleStoryIds = stories?.length && isBulk ? getEligibleAIStoryIds(stories) : targetStoryIds;
  const skippedStories = isBulk ? stories?.filter((story) => !isStoryEligibleForAIGeneration(story)) ?? [] : [];
  const skippedCount = isBulk ? Math.max(targetStoryIds.length - eligibleStoryIds.length, 0) : 0;
  const showBulkSkippedStories = isBulk && stories?.length && skippedCount > 0;

  const onConfirm = async () => {
    if (!eligibleStoryIds.length) {
      dispatch(notifyError("AI summaries can only be generated for interviews with completed transcript processing."));
      return;
    }

    setIsLoading(true);
    try {
      const prompts = isCustomPrompt ? { system: systemPrompt, user: userPrompt } : null;
      useMixPanel("create_ai_summary", {
        stories: eligibleStoryIds,
        system_prompt: prompts?.system,
        user_prompt: prompts?.user,
      });
      const results = await Promise.allSettled(eligibleStoryIds.map((id) => requestSummaryGeneration(id, prompts)));
      const failedCount = results.filter((result) => result.status === "rejected").length;
      const successCount = eligibleStoryIds.length - failedCount;

      if (successCount > 0) {
        const skippedMessage = skippedCount ? ` ${skippedCount} interview(s) were skipped.` : "";
        dispatch(
          notifySuccess(
            `${successCount} ${
              successCount > 1 ? "summaries were" : "summary was"
            } queued successfully.${skippedMessage}`
          )
        );
        onComplete?.();
        onCancel();
      }

      if (failedCount > 0) {
        dispatch(notifyError(`Error starting summary generation for ${failedCount} interview(s). Please try again.`));
      }
    } catch (e) {
      console.log(e);
      dispatch(notifyError("Error starting AI summary generation."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={showModal}
      onClose={onCancel}
      sx={{
        top: "20%",
        margin: "auto",
        maxWidth: "650px",
        outline: "none",
        border: "none",
        "&:focus": {
          outline: "none",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        rowGap="16px"
        sx={{ background: "#FFF", maxWidth: "80%", margin: "5% auto", padding: "24px" }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontSize: "20px",
            lineHeight: "32px",
            fontWeight: 500,
          }}
        >
          Create AI Summary
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: 400,
          }}
        >
          This will auto-generate a summary for{" "}
          {isBulk ? `${targetStoryIds.length} selected interviews` : "the recording"}. Any existing summary will be
          overwritten. The process may take a few minutes or so.
        </Typography>
        {showBulkSkippedStories && (
          <Box display="flex" flexDirection="column" rowGap="8px">
            <Typography variant="body2" sx={{ color: "#606971", fontSize: "14px", lineHeight: "20px" }}>
              {eligibleStoryIds.length} interview(s) are ready for summary generation. {skippedCount} interview(s) will
              be skipped because transcript is not complete.
            </Typography>
            <Box
              sx={{
                maxHeight: "120px",
                overflowY: "auto",
                border: "1px solid #D9DDE1",
                borderRadius: "4px",
                padding: "8px 12px",
                backgroundColor: "#F8F9FA",
              }}
            >
              {skippedStories.map((story) => (
                <Typography
                  key={story._id}
                  variant="body2"
                  sx={{ color: "#606971", fontSize: "13px", lineHeight: "20px" }}
                >
                  {getStoryDisplayTitle(story)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        {userFlags?.enableChatGpt && (
          <>
            <Box
              display="flex"
              columnGap="8px"
              alignItems="center"
              width="fit-content"
              sx={{ cursor: "pointer" }}
              onClick={() => setIsCustomPrompt((prev) => !prev)}
            >
              <Checkbox style={{ color: "#606971", width: "20px", height: "20px" }} checked={isCustomPrompt} />
              <Typography fontWeight={700} fontSize="14px" lineHeight="16px" color="#606971">
                I want to customize my prompt
              </Typography>
            </Box>
            {isCustomPrompt && (
              <Box display="flex" flexDirection="column" rowGap="16px">
                <TextField
                  label="System Prompt"
                  variant="outlined"
                  size="small"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  sx={{
                    width: "100%",
                    height: "40px",
                    borderRadius: "4px",
                  }}
                />
                <TextField
                  label="User Prompt"
                  variant="outlined"
                  size="small"
                  value={userPrompt}
                  multiline={true}
                  minRows={2}
                  maxRows={5}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  InputProps={{ className: classes.textArea }}
                />
              </Box>
            )}
          </>
        )}
        <Box sx={{ width: "100%", gap: 20 }}>
          <Grid container sx={{ alignContent: "center" }}>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TSSecondaryButton text="Cancel" onClick={onCancel} styles={{ marginRight: "10px" }} />
              <Button
                onClick={onConfirm}
                disabled={!eligibleStoryIds.length || (isCustomPrompt && (!systemPrompt || !userPrompt)) || isLoading}
                sx={{
                  textTransform: "none",
                  width: "180px",
                  "&:hover": {
                    outline: "none",
                  },
                  "&:focus": {
                    outline: "none",
                  },
                }}
                variant="contained"
              >
                {isLoading ? (
                  <CircularProgress size="20px" style={{ color: "#FFF" }} />
                ) : isBulk ? (
                  "Generate Summaries"
                ) : (
                  "Overwrite Summary"
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};
